import { io } from 'socket.io-client';
import useNotificationsStore from '../store/notificationsStore.js';
import useAuthStore from '../store/authStore.js';
import useCommunityStore from '../store/communityStore.js';
import useMarketplaceStore from '../store/marketplaceStore.js';
import useWalletStore from '../store/walletStore.js';
import useVTUStore from '../store/vtuStore.js';
import useBillsStore from '../store/billsStore.js';
import { toast } from 'react-hot-toast';

class RealtimeService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.eventHandlers = new Map();
  }

  connect() {
    // Try both storage methods directly as fallback
    let token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
    let user = useAuthStore.getState().user;
    
    // If store user is empty but we have token, try to get user from storage
    if (!user && token) {
      const storedUser = localStorage.getItem('user') || sessionStorage.getItem('user');
      if (storedUser) {
        try {
          user = JSON.parse(storedUser);
          console.log('Retrieved user from storage for socket connection');
        } catch (e) {
          console.error('Failed to parse stored user:', e);
        }
      }
    }

    if (!token || !user) {
      console.warn('Cannot connect to socket: No token or user');
      // Retry after 2 seconds (auth might still be hydrating)
      setTimeout(() => {
        if (useAuthStore.getState().isAuthenticated) {
          console.log('Retrying socket connection...');
          this.connect();
        }
      }, 2000);
      return;
    }

    // Disconnect existing socket if any
    if (this.socket) {
      this.disconnect();
    }

    const socketUrl = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';
    
    this.socket = io(socketUrl, {
      auth: { token },
      query: { userId: user.id },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });

    this.setupEventListeners();
  }

  setupEventListeners() {
    if (!this.socket) return;

    // Connection events
    this.socket.on('connect', () => {
      console.log('Socket connected');
      this.isConnected = true;
      this.reconnectAttempts = 0;
      
      // Subscribe to user's personal channel
      const user = useAuthStore.getState().user;
      if (user) {
        this.subscribeToUser(user.id);
      }
    });

    this.socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      this.isConnected = false;
      
      if (reason === 'io server disconnect') {
        // Server initiated disconnect, try to reconnect
        setTimeout(() => {
          this.socket.connect();
        }, 1000);
      }
    });

    this.socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      this.isConnected = false;
      this.reconnectAttempts++;
      
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.warn('Max reconnect attempts reached');
        this.disconnect();
      }
    });

    this.socket.on('reconnect', (attempt) => {
      console.log(`Reconnected after ${attempt} attempts`);
      this.isConnected = true;
      this.reconnectAttempts = 0;
    });

    // Heartbeat
    this.socket.on('pong', () => {
      console.debug('Heartbeat received');
    });

    // ========== NOTIFICATION EVENTS ==========
    this.socket.on('notification:new', (notification) => {
      console.log('New notification received:', notification);
      useNotificationsStore.getState().addNotification(notification);
      // Update unread count for notification bell
      useNotificationsStore.getState().getUnreadCount();
      
      // Show desktop notification if browser supports it
      this.showDesktopNotification(notification);
    });

    this.socket.on('notification:read', (data) => {
      console.log('Notification read:', data);
      useNotificationsStore.getState().markAsRead(data.notificationId);
      // Update unread count
      useNotificationsStore.getState().getUnreadCount();
    });

    this.socket.on('notification:read:all', (data) => {
      console.log('All notifications read:', data);
      // Update store to mark all as read
      useNotificationsStore.getState().getUnreadCount();
    });

    // ========== COMMUNITY EVENTS ==========
    this.socket.on('post:new', (post) => {
      console.log('New post received:', post);
      useCommunityStore.getState().addRealTimePost(post);
    });

    this.socket.on('post:liked', (data) => {
      console.log('Post liked:', data);
      useCommunityStore.getState().updatePostLikes(data.postId, data.userId, 'liked');
    });

    this.socket.on('post:unliked', (data) => {
      console.log('Post unliked:', data);
      useCommunityStore.getState().updatePostLikes(data.postId, data.userId, 'unliked');
    });

    this.socket.on('comment:new', (comment) => {
      console.log('New comment:', comment);
      useCommunityStore.getState().addRealTimeComment(comment);
      
      // Show notification if it's not your own comment
      const currentUser = useAuthStore.getState().user;
      if (currentUser && comment.userId !== currentUser.id) {
        this.showDesktopNotification({
          title: `New comment from ${comment.user.firstName}`,
          message: comment.content.substring(0, 100),
          type: 'comment'
        });
      }
    });

    this.socket.on('message:new', (message) => {
      console.log('New message:', message);
      useCommunityStore.getState().addRealTimeMessage(message);
      
      // Show notification if it's not your own message
      const currentUser = useAuthStore.getState().user;
      if (currentUser && message.senderId !== currentUser.id) {
        this.showDesktopNotification({
          title: `New message from ${message.sender.firstName}`,
          message: message.content.substring(0, 100),
          type: 'message'
        });
      }
    });

    this.socket.on('message:sent', (message) => {
      console.log('Message sent:', message);
      useCommunityStore.getState().addRealTimeMessage(message);
    });

    this.socket.on('user:followed', (data) => {
      console.log('User followed:', data);
      useCommunityStore.getState().updateFollowStatus(data.followerId, data.followingId, true);
      
      // Show notification
      const currentUser = useAuthStore.getState().user;
      if (currentUser && data.followingId === currentUser.id) {
        this.showDesktopNotification({
          title: 'New Follower!',
          message: `${data.follower.firstName} ${data.follower.lastName} started following you`,
          type: 'follow'
        });
      }
    });

    this.socket.on('user:unfollowed', (data) => {
      console.log('User unfollowed:', data);
      useCommunityStore.getState().updateFollowStatus(data.followerId, data.followingId, false);
    });

    // ========== MARKETPLACE EVENTS ==========
    this.socket.on('order:new', (order) => {
      console.log('New order received:', order);
      useMarketplaceStore.getState().addRealTimeOrder(order);
      
      this.showDesktopNotification({
        title: '🛍️ New Order!',
        message: `You have a new order worth ₦${order.totalAmount?.toLocaleString()}`,
        type: 'marketplace'
      });
    });

    this.socket.on('order:business:new', (data) => {
      console.log('New business order:', data);
      useMarketplaceStore.getState().updateOrderCounts(data.businessId, data);
    });

    this.socket.on('order:status:updated', (data) => {
      console.log('Order status updated:', data);
      useMarketplaceStore.getState().updateOrderStatusRealTime(data.orderId, data.status, data.previousStatus);
      
      if (data.status === 'COMPLETED') {
        this.showDesktopNotification({
          title: '✅ Order Completed!',
          message: `Order #${data.orderNumber} has been completed`,
          type: 'marketplace'
        });
      }
    });

    this.socket.on('order:business:updated', (data) => {
      console.log('Business order updated:', data);
      useMarketplaceStore.getState().updateOrderStatusRealTime(data.orderId, data.status);
    });

    this.socket.on('order:completed', (data) => {
      console.log('Order completed:', data);
      this.showDesktopNotification({
        title: '🎉 Order Completed!',
        message: `Your order from ${data.businessName} is complete`,
        type: 'marketplace'
      });
    });

    this.socket.on('product:new', (product) => {
      console.log('New product:', product);
      useMarketplaceStore.getState().addRealTimeProduct(product);
    });

    this.socket.on('product:updated', (product) => {
      console.log('Product updated:', product);
      useMarketplaceStore.getState().updateProductRealTime(product.productId, product);
    });

    this.socket.on('inventory:updated', (data) => {
      console.log('Inventory updated:', data);
      useMarketplaceStore.getState().updateProductRealTime(data.productId, {
        quantity: data.quantity,
        status: data.status
      });
    });

    this.socket.on('inventory:alert', (alert) => {
      console.log('Inventory alert:', alert);
      useMarketplaceStore.getState().addInventoryAlert(alert);
      
      this.showDesktopNotification({
        title: '⚠️ Low Stock Alert',
        message: alert.message || `${alert.productName} is running low (${alert.quantity} left)`,
        type: 'marketplace'
      });
    });

    this.socket.on('marketplace:message:new', (message) => {
      console.log('New marketplace message:', message);
      useMarketplaceStore.getState().addMarketplaceMessage(message);
      
      const currentUser = useAuthStore.getState().user;
      if (currentUser && message.senderId !== currentUser.id) {
        this.showDesktopNotification({
          title: `💬 Message from ${message.sender.firstName}`,
          message: message.content.substring(0, 100),
          type: 'marketplace'
        });
      }
    });

    this.socket.on('marketplace:message:sent', (message) => {
      console.log('Marketplace message sent:', message);
      useMarketplaceStore.getState().addMarketplaceMessage(message);
    });

    // ========== BUSINESS RENEWAL EVENTS ==========
    this.socket.on('business:renewed', (data) => {
      console.log('Business renewed:', data);
      const marketplaceStore = useMarketplaceStore.getState();
      marketplaceStore.updateBusinessExpiryRealTime(data);
      
      // Show toast notification
      toast.success(`✅ ${data.businessName} renewed for another 24 hours!`);
      
      // Also show desktop notification
      this.showDesktopNotification({
        title: '🔄 Business Renewed',
        message: `${data.businessName} has been renewed for another 24 hours!`,
        type: 'marketplace'
      });
    });

    this.socket.on('business:created', (data) => {
      console.log('Business created:', data);
      toast.success(`🎉 ${data.businessName} is now live for 24 hours!`);
      
      // Show desktop notification
      this.showDesktopNotification({
        title: '🎉 Business Created',
        message: `${data.businessName} is now live on the marketplace!`,
        type: 'marketplace'
      });
    });

    this.socket.on('business:deleted', (data) => {
      console.log('Business deleted:', data);
      const marketplaceStore = useMarketplaceStore.getState();
      marketplaceStore.clearUserBusiness();
      toast.info(`Business deleted`);
      
      // Show desktop notification
      this.showDesktopNotification({
        title: 'Business Deleted',
        message: data.message || 'Your business has been deleted',
        type: 'marketplace'
      });
    });

    // ========== WALLET EVENTS ==========
    this.socket.on('wallet:balance:updated', (data) => {
      console.log('Wallet balance updated:', data);
      useWalletStore.getState().updateBalanceRealTime(data);
    });

    this.socket.on('wallet:transaction:new', (transaction) => {
      console.log('New wallet transaction:', transaction);
      useWalletStore.getState().addTransactionRealTime(transaction);
      
      // Show notification for significant transactions
      if (transaction.amount > 10000) {
        this.showDesktopNotification({
          title: transaction.type === 'CREDIT' ? '💰 Wallet Credited' : '💸 Wallet Debited',
          message: `${transaction.type === 'CREDIT' ? '+' : '-'}₦${transaction.amount.toLocaleString()}: ${transaction.description || 'Transaction completed'}`,
          type: 'wallet'
        });
      }
    });

    this.socket.on('wallet:transaction:success', (data) => {
      console.log('Transaction success:', data);
      useWalletStore.getState().handleTransactionSuccess(data);
      
      toast.success(data.description || 'Transaction successful');
      this.showDesktopNotification({
        title: '✅ Transaction Successful',
        message: `${data.description || 'Transaction'} completed successfully`,
        type: 'wallet'
      });
    });

    this.socket.on('wallet:transaction:failed', (data) => {
      console.log('Transaction failed:', data);
      useWalletStore.getState().handleTransactionFailed(data);
      
      toast.error(data.reason || 'Transaction failed');
      this.showDesktopNotification({
        title: '❌ Transaction Failed',
        message: data.reason || 'Your transaction could not be completed',
        type: 'wallet'
      });
    });

    this.socket.on('wallet:funded', (data) => {
      console.log('Wallet funded:', data);
      // Just refresh wallet data
      useWalletStore.getState().getWallet();
      
      toast.success(`₦${data.amount.toLocaleString()} added to wallet`);
      this.showDesktopNotification({
        title: '💰 Wallet Funded',
        message: `₦${data.amount.toLocaleString()} has been added to your wallet`,
        type: 'wallet'
      });
    });

    // ========== VTU EVENTS ==========
    this.socket.on('vtu:transaction:pending', (data) => {
      console.log('VTU transaction pending:', data);
      useVTUStore.getState().handleTransactionPending(data);
      
      toast.loading(`${data.serviceType} processing...`, { id: data.transactionId });
    });

    this.socket.on('vtu:transaction:success', (data) => {
      console.log('VTU transaction success:', data);
      useVTUStore.getState().handleTransactionSuccess(data);
      
      // Show toast notification
      toast.success(`${data.serviceType} of ₦${data.amount?.toLocaleString()} to ${data.recipient} completed`, { id: data.transactionId });
      
      // Show desktop notification
      this.showDesktopNotification({
        title: `📱 ${data.serviceType} Successful`,
        message: `${data.serviceType} of ₦${data.amount?.toLocaleString()} to ${data.recipient} completed`,
        type: 'vtu'
      });
    });

    this.socket.on('vtu:transaction:failed', (data) => {
      console.log('VTU transaction failed:', data);
      useVTUStore.getState().handleTransactionFailed(data);
      
      // Show toast notification
      toast.error(data.reason || `${data.serviceType} purchase failed`, { id: data.transactionId });
      
      // Show desktop notification
      this.showDesktopNotification({
        title: `❌ ${data.serviceType} Failed`,
        message: data.reason || `Your ${data.serviceType} purchase could not be completed`,
        type: 'vtu'
      });
    });

    this.socket.on('vtu:transaction:retry:success', (data) => {
      console.log('VTU retry success:', data);
      useVTUStore.getState().handleRetrySuccess(data);
      
      toast.success(`Retry successful: ${data.serviceType} completed`);
      this.showDesktopNotification({
        title: `🔄 Retry Successful`,
        message: `Your ${data.serviceType} transaction has been completed`,
        type: 'vtu'
      });
    });

    this.socket.on('vtu:transaction:retry:failed', (data) => {
      console.log('VTU retry failed:', data);
      useVTUStore.getState().handleRetryFailed(data);
      
      toast.error(data.reason || 'Retry attempt failed');
      this.showDesktopNotification({
        title: `❌ Retry Failed`,
        message: data.reason || 'Retry attempt failed',
        type: 'vtu'
      });
    });

    this.socket.on('vtu:network:detected', (data) => {
      console.log('Network detected:', data);
      // This can be used to auto-select network in UI
    });

    // ========== BILLS EVENTS ==========
    this.socket.on('bills:transaction:pending', (data) => {
      console.log('Bills transaction pending:', data);
      useBillsStore.getState().handleTransactionPending(data);
      
      toast.loading(`${data.billType || 'Bill'} payment processing...`, { id: data.transactionId });
    });

    this.socket.on('bills:transaction:success', (data) => {
      console.log('Bills transaction success:', data);
      useBillsStore.getState().handleTransactionSuccess(data);
      
      // Show toast notification
      let message = '';
      if (data.billType === 'ELECTRICITY') {
        message = `Electricity payment of ₦${data.amount?.toLocaleString()} to ${data.customerName || data.recipient} completed`;
        if (data.token) {
          message += `\nToken: ${data.token}`;
        }
      } else {
        message = `${data.packageName || 'TV subscription'} of ₦${data.amount?.toLocaleString()} completed`;
      }
      
      toast.success(message, { id: data.transactionId, duration: 5000 });
      
      // Show desktop notification
      this.showDesktopNotification({
        title: `⚡ ${data.billType === 'ELECTRICITY' ? 'Electricity' : 'TV'} Payment Successful`,
        message: message,
        type: 'bills'
      });
    });

    this.socket.on('bills:transaction:failed', (data) => {
      console.log('Bills transaction failed:', data);
      useBillsStore.getState().handleTransactionFailed(data);
      
      toast.error(data.reason || `Payment failed`, { id: data.transactionId });
      this.showDesktopNotification({
        title: `❌ ${data.billType || 'Bill'} Payment Failed`,
        message: data.reason || `Your ${data.billType || 'bill'} payment could not be completed`,
        type: 'bills'
      });
    });

    this.socket.on('bills:transaction:retry:success', (data) => {
      console.log('Bills retry success:', data);
      useBillsStore.getState().handleRetrySuccess(data);
      
      toast.success(`Retry successful: ${data.billType} payment completed`);
      this.showDesktopNotification({
        title: `🔄 Retry Successful`,
        message: `Your ${data.billType} transaction has been completed`,
        type: 'bills'
      });
    });

    this.socket.on('bills:transaction:retry:failed', (data) => {
      console.log('Bills retry failed:', data);
      useBillsStore.getState().handleRetryFailed(data);
      
      toast.error(data.reason || 'Retry attempt failed');
      this.showDesktopNotification({
        title: `❌ Retry Failed`,
        message: data.reason || 'Retry attempt failed',
        type: 'bills'
      });
    });

    this.socket.on('bills:meter:validated', (data) => {
      console.log('Meter validated:', data);
      useBillsStore.getState().addRecentMeter(data.meterNumber);
    });

    this.socket.on('bills:smartcard:validated', (data) => {
      console.log('Smartcard validated:', data);
      useBillsStore.getState().addRecentSmartcard(data.smartcardNumber);
    });

    // Custom event handlers
    this.eventHandlers.forEach((handler, event) => {
      this.socket.on(event, handler);
    });
  }

  // Show desktop notification
  showDesktopNotification(notification) {
    if (!('Notification' in window)) return;
    
    if (Notification.permission === 'granted') {
      try {
        // Check if we have a service worker (mobile requires this)
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
          navigator.serviceWorker.ready.then(registration => {
            registration.showNotification(notification.title || 'Fastel', {
              body: notification.message || notification.body,
              icon: '/fastel-logo.png',
              tag: notification.id || Date.now().toString(),
              silent: false,
              vibrate: [200, 100, 200], // Added for mobile vibration
            });
          });
        } 
        // Fallback for desktop without service worker
        else {
          new Notification(notification.title || 'Fastel', {
            body: notification.message || notification.body,
            icon: '/fastel-logo.png',
            tag: notification.id || Date.now().toString(),
            silent: false,
          });
        }
      } catch (error) {
        console.warn('Failed to show notification:', error);
      }
    }
  }

  disconnect() {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      console.log('Socket disconnected');
    }
  }

  emit(event, data) {
    if (this.socket && this.isConnected) {
      this.socket.emit(event, data);
    } else {
      console.warn('Cannot emit event: Socket not connected');
    }
  }

  // Request notification permission
  requestNotificationPermission() {
    if ('Notification' in window) {
      if (Notification.permission === 'default') {
        Notification.requestPermission().then(permission => {
          if (permission === 'granted') {
            console.log('Notification permission granted');
          }
        });
      }
    }
  }

  // Subscribe to specific events
  subscribeToUser(userId) {
    this.emit('subscribe:user', { userId });
  }

  subscribeToPost(postId) {
    this.emit('subscribe:post', { postId });
  }

  subscribeToConversation(userId) {
    this.emit('subscribe:conversation', { userId });
  }

  // Unsubscribe from events
  unsubscribeFromUser(userId) {
    this.emit('unsubscribe:user', { userId });
  }

  unsubscribeFromPost(postId) {
    this.emit('unsubscribe:post', { postId });
  }

  unsubscribeFromConversation(userId) {
    this.emit('unsubscribe:conversation', { userId });
  }

  // Add custom event handler
  on(event, handler) {
    this.eventHandlers.set(event, handler);
    if (this.socket) {
      this.socket.on(event, handler);
    }
  }

  // Remove custom event handler
  off(event) {
    this.eventHandlers.delete(event);
    if (this.socket) {
      this.socket.off(event);
    }
  }

  // Check if connected
  isConnected() {
    return this.isConnected;
  }

  // Get socket id
  getSocketId() {
    return this.socket?.id;
  }
}

export default new RealtimeService();